import * as Sentry from "@sentry/react-native";
import { useField, useForm } from "mobx-easy-form";
import { observer } from "mobx-react";
import React from "react";
import * as yup from "yup";
import { Spacer } from "~/components/Spacer";
import { TextInputV2 } from "~/components/V2/TextInputV2";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import type { UserInstance } from "~/mobx/entities/user/User";
import { useStore } from "~/mobx/utils/useStore";
import { EditPetModal } from "../my-pets/EditPetModal";

export const EditAddressModal = observer(function EditAddressModal({
  user,
  isVisible,
  onClose,
}: {
  user: UserInstance;
  isVisible: boolean;
  onClose: () => void;
}) {
  const errorAlert = useErrorAlertV2();
  const store = useStore();

  const form = useForm({
    onSubmit: async ({ values }) => {
      const { address, city, zipCode } = values as {
        address: string;
        city: string;
        zipCode: string;
      };

      try {
        await store.userStore.updateActiveUser({
          streetAddress: address,
          city: city,
          zipCode: zipCode,
        });
        onClose();
      } catch (error) {
        console.log("Error while updating mobile phone", error);
        errorAlert(error);
        Sentry.captureException(error, {
          extra: {
            payload: {
              streetAddress: address,
              city: city,
              zipCode: zipCode,
            },
          },
        });
      }
    },
  });

  const address = useField({
    form,
    id: "address",
    initialValue: user.streetAddress ?? "",
    validationSchema: yup
      .string()
      .required()
      .test(
        "not-only-spaces",
        "Cannot be only spaces.",
        (value) => !!value && value.trim().length > 0,
      ),
  });

  const city = useField({
    form,
    id: "city",
    initialValue: user.city ?? "",
    validationSchema: yup
      .string()
      .required()
      .test(
        "not-only-spaces",
        "Cannot be only spaces.",
        (value) => !!value && value.trim().length > 0,
      ),
  });

  const zipCode = useField({
    form,
    id: "zipCode",
    initialValue: user.zipCode ?? "",
    validationSchema: yup
      .string()
      .required()
      .test(
        "not-only-spaces",
        "Cannot be only spaces.",
        (value) => !!value && value.trim().length > 0,
      ),
  });

  return (
    <>
      {isVisible && (
        <EditPetModal
          onSave={form.actions.submit}
          onClose={onClose}
          title={intl.formatMessage({
            id: "FtPNTU",
            defaultMessage: "Update your address",
            description: "Update your address title",
          })}
        >
          <View borderRadius8 style={{ overflow: "hidden" }}>
            <TextInputV2
              value={address.state.value}
              onChangeText={address.actions.onChange}
              onFocus={address.actions.onFocus}
              onBlur={address.actions.onBlur}
              label={intl.formatMessage({
                id: "FCesIR",
                defaultMessage: "Address",
                description: "Adderss label",
              })}
              isError={!!address.computed.error}
            />

            <Spacer extraLarge />

            <TextInputV2
              value={city.state.value}
              onChangeText={city.actions.onChange}
              onFocus={city.actions.onFocus}
              onBlur={city.actions.onBlur}
              label={intl.formatMessage({
                id: "mMWP1r",
                defaultMessage: "City",
                description: "City label",
              })}
              isError={!!city.computed.error}
            />

            <Spacer extraLarge />

            <TextInputV2
              value={zipCode.state.value}
              onChangeText={zipCode.actions.onChange}
              onFocus={zipCode.actions.onFocus}
              onBlur={zipCode.actions.onBlur}
              label={intl.formatMessage({
                id: "qSSaPW",
                defaultMessage: "Zip code",
                description: "Zip code label",
              })}
              isError={!!zipCode.computed.error}
            />
          </View>
        </EditPetModal>
      )}
    </>
  );
});
